import { CreditType as CreditTypeConst, ProvinceCodes } from '@/constants';
import { Certificate, CreditType, YearlySummary } from '@/types';

import { MIN_CPD_YEAR } from './constants';

export const filterCredits = ({
  credits,
  selectedFilter,
  searchTerm,
}: {
  credits: Certificate[];
  selectedFilter: CreditType | 'all';
  searchTerm: string;
}) => {
  return credits
    .filter(
      certificate =>
        selectedFilter === 'all' || certificate.type === selectedFilter,
    )
    .filter(certificate => {
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        return (
          certificate.title.toLowerCase().includes(searchTermLower) ||
          certificate.fieldsOfStudy?.[0].name
            ?.toLowerCase()
            .includes(searchTermLower) ||
          certificate.provider?.toLowerCase().includes(searchTermLower) ||
          certificate.otherProvider?.toLowerCase().includes(searchTermLower) ||
          certificate.deliveryMethod?.toLowerCase().includes(searchTermLower) ||
          certificate.episodeId?.toLowerCase().includes(searchTermLower)
        );
      }
      return true;
    });
};

export const selectPDHourTotalsCanada = (credits: Certificate[]) =>
  credits.reduce(
    (totals, credit) => ({
      total: totals.total + credit.credit,
      ethics: totals.ethics + credit.ethicsCredit,
      lumiq:
        credit.type === CreditTypeConst.LUMIQ
          ? totals.lumiq + credit.credit
          : totals.lumiq,
      lumiqEthics:
        credit.type === CreditTypeConst.LUMIQ
          ? totals.lumiqEthics + credit.ethicsCredit
          : totals.lumiqEthics,
      external:
        credit.type === CreditTypeConst.CUSTOM
          ? totals.external + credit.credit
          : totals.external,
      externalEthics:
        credit.type === CreditTypeConst.CUSTOM
          ? totals.externalEthics + credit.ethicsCredit
          : totals.externalEthics,
    }),
    {
      total: 0,
      ethics: 0,
      lumiq: 0,
      lumiqEthics: 0,
      external: 0,
      externalEthics: 0,
    },
  );

export const selectCPDTrackerCurrentDateRange = (province?: string) => {
  const currentYear = new Date().getFullYear();
  const isCurrentMonthAfterAug = new Date().getMonth() > 7;
  if (province === ProvinceCodes.QC) {
    return {
      fromDate: `${isCurrentMonthAfterAug ? currentYear : currentYear - 1}-09-01`,
      toDate: `${isCurrentMonthAfterAug ? currentYear + 1 : currentYear}-08-31`,
    };
  }
  return {
    fromDate: `${currentYear}-01-01`,
    toDate: `${currentYear}-12-31`,
  };
};

export const selectCPDDateSelectorOptions = (province?: string) => {
  const options = [];
  const currentYear = new Date().getFullYear();
  let year = currentYear + 1; // records are allowed to be created for next year too

  while (year > MIN_CPD_YEAR) {
    if (province === ProvinceCodes.QC) {
      options.push({
        fromDate: `${year}-09-01`,
        toDate: `${year + 1}-08-31`,
      });
    } else {
      options.push({
        fromDate: `${year}-01-01`,
        toDate: `${year}-12-31`,
      });
    }
    year -= 1;
  }

  return options;
};

export const formatCPDDateRangeLabel = ({
  province,
  year,
}: {
  province?: string;
  year: number;
}) => {
  if (province === ProvinceCodes.QC) {
    return `Sept. ${year} to Aug. ${year + 1}`;
  } else {
    return year.toString();
  }
};

export const formatTriennialDateRangeLabel = ({
  province,
  yearlySummary,
}: {
  province: string;
  yearlySummary: YearlySummary[];
}) => {
  if (province === ProvinceCodes.QC) {
    return {
      fromDate: {
        label: `Sept. ${yearlySummary[0].baseYear}`,
        value: yearlySummary[0].dateStart,
      },
      toDate: {
        label: `Aug. ${yearlySummary[2].baseYear + 1}`,
        value: yearlySummary[2].dateEnd,
      },
    };
  }
  return {
    fromDate: {
      label: yearlySummary[0].baseYear,
      value: yearlySummary[0].dateStart,
    },
    toDate: {
      label: yearlySummary[2].baseYear,
      value: yearlySummary[2].dateEnd,
    },
  };
};
